import { useTranslation } from 'react-i18next';
import {
  WebFilterFileSlot,
  WebFilterPlacementKind,
  WebFilterPlacementSection,
} from '@/api/generated/schemas';
import { Checkbox, Field, Panel, Select, Switch } from '@/shared/ui';
import { cn } from '@/shared/lib/utils';
import { BANNER_BADGES } from '../../internal/bannerBadges';
import type { PlacementFormValue } from '../../internal/placementDefaults';
import type { WebFilterFormApi } from '../useWebFilterForm';
import { AssetFileField } from './AssetFileField';

const LABEL_W = 140;

export interface WebDisplayPanelProps {
  form: WebFilterFormApi;
}

function updatePlacement(
  placements: PlacementFormValue[],
  kind: (typeof WebFilterPlacementKind)[keyof typeof WebFilterPlacementKind],
  patch: Partial<PlacementFormValue>
): PlacementFormValue[] {
  return placements.map((p) => (p.kind === kind ? { ...p, ...patch } : p));
}

function SectionToggleRow({
  title,
  enabled,
  onToggle,
  summary,
  children,
  offNote,
}: {
  title: string;
  enabled: boolean;
  onToggle: (next: boolean) => void;
  summary?: string;
  children: React.ReactNode;
  offNote: string;
}) {
  const { t } = useTranslation('www');

  return (
    <div className="border-phx-border-1 gap-phx-3 pt-phx-4 flex border-t first:border-t-0 first:pt-0">
      <div className="w-[150px] shrink-0">
        <div className="text-phx-sm text-phx-text font-bold">{title}</div>
        <div className="gap-phx-2 mt-phx-2 flex items-center">
          <Switch checked={enabled} onCheckedChange={onToggle} />
          <span
            className={cn(
              'text-phx-xs font-extrabold uppercase',
              enabled ? 'text-phx-success' : 'text-phx-text-faint'
            )}
          >
            {enabled ? t('webFilter.display.on') : t('webFilter.display.off')}
          </span>
        </div>
        {enabled && summary ? (
          <div className="text-phx-xs text-phx-primary mt-phx-1 font-bold">{summary}</div>
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        {enabled ? children : <p className="text-phx-xs text-phx-text-faint pt-phx-2">{offNote}</p>}
      </div>
    </div>
  );
}

function PlacementBox({
  checked,
  onCheckedChange,
  title,
  description,
  children,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  title: string;
  description: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'border-phx-border-2 mb-phx-2 rounded-phx p-phx-3 border',
        checked && 'border-phx-primary bg-phx-primary-tint/30'
      )}
    >
      <label className="gap-phx-2 flex cursor-pointer items-start">
        <Checkbox checked={checked} onCheckedChange={onCheckedChange} />
        <span className="min-w-0 flex-1">
          <span className="text-phx-sm text-phx-text block font-extrabold">{title}</span>
          <span className="text-phx-xs text-phx-text-muted block">{description}</span>
        </span>
      </label>
      {checked && children ? (
        <div className="mt-phx-3 gap-phx-2 flex flex-col">{children}</div>
      ) : null}
    </div>
  );
}

/** Panel Web display — Main menu / Search filter / Home page dle prototypu. */
export function WebDisplayPanel({ form }: WebDisplayPanelProps) {
  const { t } = useTranslation('www');

  return (
    <Panel
      variant="filled"
      icon="globe"
      title={t('webFilter.display.title')}
      subtitle={t('webFilter.display.subtitle')}
    >
      <form.Subscribe selector={(s) => s.values}>
        {(values) => {
          const placements = values.placements;
          const setPlacements = (next: PlacementFormValue[]) =>
            form.setFieldValue('placements', next);

          const banner = placements.find((p) => p.kind === WebFilterPlacementKind.MenuBanner)!;
          const tile = placements.find((p) => p.kind === WebFilterPlacementKind.MenuTile)!;
          const special = placements.find((p) => p.kind === WebFilterPlacementKind.MenuSpecial)!;
          const link = placements.find((p) => p.kind === WebFilterPlacementKind.MenuLink)!;
          const searchSpecial = placements.find(
            (p) => p.kind === WebFilterPlacementKind.SearchSpecialPeriods
          )!;
          const searchType = placements.find(
            (p) => p.kind === WebFilterPlacementKind.SearchTourTypes
          )!;
          const hp = placements.find((p) => p.kind === WebFilterPlacementKind.HomeFindTour)!;

          const menuSummary = [
            banner.isEnabled && t('webFilter.display.modes.banner'),
            tile.isEnabled && t('webFilter.display.modes.tile'),
            special.isEnabled && t('webFilter.display.modes.special'),
            link.isEnabled && t('webFilter.display.modes.link'),
          ]
            .filter(Boolean)
            .join(' + ');

          const searchSummary = searchSpecial.isEnabled
            ? t('webFilter.display.search.special')
            : searchType.isEnabled
              ? t('webFilter.display.search.tourTypes')
              : '';

          const enableMenuKind = (
            kind: (typeof WebFilterPlacementKind)[keyof typeof WebFilterPlacementKind],
            next: boolean
          ) => {
            let updated = updatePlacement(placements, kind, { isEnabled: next });
            if (next && !values.sectionMenuEnabled) {
              form.setFieldValue('sectionMenuEnabled', true);
            }
            setPlacements(updated);
          };

          const setSearchKind = (
            kind:
              | typeof WebFilterPlacementKind.SearchSpecialPeriods
              | typeof WebFilterPlacementKind.SearchTourTypes
          ) => {
            let updated = placements.map((p) => {
              if (p.section !== WebFilterPlacementSection.SearchFilter) return p;
              return { ...p, isEnabled: p.kind === kind };
            });
            form.setFieldValue('sectionSearchEnabled', true);
            setPlacements(updated);
          };

          return (
            <div className="gap-phx-4 flex flex-col">
              <SectionToggleRow
                title={t('webFilter.display.sections.menu')}
                enabled={values.sectionMenuEnabled}
                onToggle={(next) => {
                  form.setFieldValue('sectionMenuEnabled', next);
                }}
                summary={menuSummary}
                offNote={t('webFilter.display.sections.menuOff')}
              >
                <PlacementBox
                  checked={banner.isEnabled}
                  onCheckedChange={(v) => enableMenuKind(WebFilterPlacementKind.MenuBanner, v)}
                  title={t('webFilter.display.modes.banner')}
                  description={t('webFilter.display.modes.bannerDesc')}
                >
                  <AssetFileField
                    fileId={banner.photoFileId}
                    webProjectId={values.webProjectId}
                    slot={WebFilterFileSlot.Banner}
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    emptyLabel={t('webFilter.display.noPhoto')}
                    uploadLabel={t('webFilter.display.uploadPhoto')}
                    onUploaded={(fileId) =>
                      setPlacements(
                        updatePlacement(placements, WebFilterPlacementKind.MenuBanner, {
                          photoFileId: fileId,
                        })
                      )
                    }
                    onClear={() =>
                      setPlacements(
                        updatePlacement(placements, WebFilterPlacementKind.MenuBanner, {
                          photoFileId: null,
                        })
                      )
                    }
                  />
                  <Field
                    layout="inline"
                    labelWidth={LABEL_W}
                    label={t('webFilter.display.bannerTitle')}
                  >
                    <input
                      className="border-phx-border-2 h-phx-control rounded-phx px-phx-3 text-phx-sm w-full border font-extrabold"
                      value={banner.labelText}
                      placeholder={t('webFilter.display.useFilterName')}
                      onChange={(e) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuBanner, {
                            labelText: e.target.value,
                          })
                        )
                      }
                    />
                  </Field>
                  <div className="gap-phx-2 flex flex-wrap">
                    <Select
                      className="w-[170px]"
                      value={(banner.payload['badge'] as string) ?? ''}
                      placeholder={t('webFilter.display.noBadge')}
                      options={BANNER_BADGES.map((b) => ({ value: b, label: b }))}
                      onChange={(e) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuBanner, {
                            payload: { ...banner.payload, badge: e.target.value || null },
                          })
                        )
                      }
                    />
                    <input
                      className="border-phx-border-2 h-phx-control rounded-phx px-phx-3 text-phx-sm min-w-[200px] flex-1 border"
                      value={banner.description}
                      placeholder={t('webFilter.display.bannerText')}
                      onChange={(e) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuBanner, {
                            description: e.target.value,
                          })
                        )
                      }
                    />
                    <input
                      className="border-phx-border-2 h-phx-control rounded-phx px-phx-3 text-phx-sm w-[140px] border"
                      value={(banner.payload['cta'] as string) ?? ''}
                      placeholder={t('webFilter.display.cta')}
                      onChange={(e) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuBanner, {
                            payload: { ...banner.payload, cta: e.target.value },
                          })
                        )
                      }
                    />
                  </div>
                  <label className="gap-phx-2 text-phx-sm flex items-center font-bold">
                    <Checkbox
                      checked={banner.showPriceFrom}
                      onCheckedChange={(v) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuBanner, {
                            showPriceFrom: v,
                          })
                        )
                      }
                    />
                    {t('webFilter.display.showPriceFrom')}
                  </label>
                </PlacementBox>

                <PlacementBox
                  checked={tile.isEnabled}
                  onCheckedChange={(v) => enableMenuKind(WebFilterPlacementKind.MenuTile, v)}
                  title={t('webFilter.display.modes.tile')}
                  description={t('webFilter.display.modes.tileDesc')}
                >
                  <Field
                    layout="inline"
                    labelWidth={LABEL_W}
                    label={t('webFilter.display.tileName')}
                  >
                    <input
                      className="border-phx-border-2 h-phx-control rounded-phx px-phx-3 text-phx-sm w-full border"
                      value={tile.labelText}
                      placeholder={t('webFilter.display.useFilterName')}
                      onChange={(e) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuTile, {
                            labelText: e.target.value,
                          })
                        )
                      }
                    />
                  </Field>
                  <label className="gap-phx-2 text-phx-sm flex items-center font-bold">
                    <Checkbox
                      checked={tile.showPriceFrom}
                      onCheckedChange={(v) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuTile, {
                            showPriceFrom: v,
                          })
                        )
                      }
                    />
                    {t('webFilter.display.showPriceFrom')}
                  </label>
                </PlacementBox>

                <PlacementBox
                  checked={special.isEnabled}
                  onCheckedChange={(v) => enableMenuKind(WebFilterPlacementKind.MenuSpecial, v)}
                  title={t('webFilter.display.modes.special')}
                  description={t('webFilter.display.modes.specialDesc')}
                >
                  <Field
                    layout="inline"
                    labelWidth={LABEL_W}
                    label={t('webFilter.display.specialName')}
                  >
                    <input
                      className="border-phx-border-2 h-phx-control rounded-phx px-phx-3 text-phx-sm w-full border"
                      value={special.labelText}
                      placeholder={t('webFilter.display.useFilterName')}
                      onChange={(e) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuSpecial, {
                            labelText: e.target.value,
                          })
                        )
                      }
                    />
                  </Field>
                </PlacementBox>

                <PlacementBox
                  checked={link.isEnabled}
                  onCheckedChange={(v) => enableMenuKind(WebFilterPlacementKind.MenuLink, v)}
                  title={t('webFilter.display.modes.link')}
                  description={t('webFilter.display.modes.linkDesc')}
                >
                  <Field
                    layout="inline"
                    labelWidth={LABEL_W}
                    label={t('webFilter.display.linkText')}
                  >
                    <input
                      className="border-phx-border-2 h-phx-control rounded-phx px-phx-3 text-phx-sm w-full border"
                      value={link.labelText}
                      placeholder={t('webFilter.display.useFilterName')}
                      onChange={(e) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.MenuLink, {
                            labelText: e.target.value,
                          })
                        )
                      }
                    />
                  </Field>
                </PlacementBox>
              </SectionToggleRow>

              <SectionToggleRow
                title={t('webFilter.display.sections.search')}
                enabled={values.sectionSearchEnabled}
                onToggle={(next) => {
                  form.setFieldValue('sectionSearchEnabled', next);
                  if (next) {
                    const anyOn = placements.some(
                      (p) => p.section === WebFilterPlacementSection.SearchFilter && p.isEnabled
                    );
                    if (!anyOn) {
                      setSearchKind(WebFilterPlacementKind.SearchSpecialPeriods);
                    }
                  }
                }}
                summary={searchSummary}
                offNote={t('webFilter.display.sections.searchOff')}
              >
                <div className="gap-phx-2 mb-1 flex flex-wrap">
                  <button
                    type="button"
                    className={cn(
                      'border-phx-border-2 rounded-phx p-phx-3 min-w-[150px] flex-1 border text-left',
                      searchSpecial.isEnabled && 'border-phx-primary bg-phx-primary-tint/30'
                    )}
                    onClick={() => setSearchKind(WebFilterPlacementKind.SearchSpecialPeriods)}
                  >
                    <div className="text-phx-xs font-extrabold">
                      {t('webFilter.display.search.special')}
                    </div>
                    <div className="text-phx-xs text-phx-text-muted">
                      {t('webFilter.display.search.specialDesc')}
                    </div>
                  </button>
                  <button
                    type="button"
                    className={cn(
                      'border-phx-border-2 rounded-phx p-phx-3 min-w-[150px] flex-1 border text-left',
                      searchType.isEnabled && 'border-phx-primary bg-phx-primary-tint/30'
                    )}
                    onClick={() => setSearchKind(WebFilterPlacementKind.SearchTourTypes)}
                  >
                    <div className="text-phx-xs font-extrabold">
                      {t('webFilter.display.search.tourTypes')}
                    </div>
                    <div className="text-phx-xs text-phx-text-muted">
                      {t('webFilter.display.search.tourTypesDesc')}
                    </div>
                    {searchType.isEnabled ? (
                      <div className="gap-phx-2 mt-phx-2 flex flex-wrap">
                        {(['highlight', 'normal'] as const).map((variant) => (
                          <button
                            key={variant}
                            type="button"
                            className={cn(
                              'border-phx-border-2 text-phx-xs px-phx-3 py-phx-1 rounded-full border font-semibold',
                              (searchType.payload['fTypeVariant'] ?? 'normal') === variant &&
                                'border-phx-primary bg-phx-primary-tint text-phx-primary'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPlacements(
                                updatePlacement(
                                  placements,
                                  WebFilterPlacementKind.SearchTourTypes,
                                  {
                                    payload: { ...searchType.payload, fTypeVariant: variant },
                                  }
                                )
                              );
                            }}
                          >
                            {variant === 'highlight'
                              ? t('webFilter.display.search.highlight')
                              : t('webFilter.display.search.normal')}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </button>
                </div>
                <Field layout="inline" labelWidth={LABEL_W} label={t('webFilter.display.boxText')}>
                  <input
                    className="border-phx-border-2 h-phx-control rounded-phx px-phx-3 text-phx-sm w-full border"
                    value={(searchSpecial.isEnabled ? searchSpecial : searchType).labelText}
                    placeholder={t('webFilter.display.useFilterName')}
                    onChange={(e) => {
                      const kind = searchSpecial.isEnabled
                        ? WebFilterPlacementKind.SearchSpecialPeriods
                        : WebFilterPlacementKind.SearchTourTypes;
                      setPlacements(
                        updatePlacement(placements, kind, { labelText: e.target.value })
                      );
                    }}
                  />
                </Field>
              </SectionToggleRow>

              <SectionToggleRow
                title={t('webFilter.display.sections.hp')}
                enabled={values.sectionHpEnabled}
                onToggle={(next) => {
                  form.setFieldValue('sectionHpEnabled', next);
                  setPlacements(
                    updatePlacement(placements, WebFilterPlacementKind.HomeFindTour, {
                      isEnabled: next,
                    })
                  );
                }}
                offNote={t('webFilter.display.sections.hpOff')}
              >
                <div className="border-phx-border-2 gap-phx-2 rounded-phx p-phx-3 flex flex-col border">
                  <div className="text-phx-sm font-extrabold">
                    {t('webFilter.display.hp.cardTitle')}
                  </div>
                  <AssetFileField
                    fileId={hp.photoFileId}
                    webProjectId={values.webProjectId}
                    slot={WebFilterFileSlot.HomePage}
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    emptyLabel={t('webFilter.display.noPhoto')}
                    uploadLabel={t('webFilter.display.uploadPhoto')}
                    onUploaded={(fileId) =>
                      setPlacements(
                        updatePlacement(placements, WebFilterPlacementKind.HomeFindTour, {
                          photoFileId: fileId,
                        })
                      )
                    }
                    onClear={() =>
                      setPlacements(
                        updatePlacement(placements, WebFilterPlacementKind.HomeFindTour, {
                          photoFileId: null,
                        })
                      )
                    }
                  />
                  <Field
                    layout="inline"
                    labelWidth={LABEL_W}
                    label={t('webFilter.display.hp.description')}
                  >
                    <input
                      className="border-phx-border-2 h-phx-control rounded-phx px-phx-3 text-phx-sm w-full border"
                      value={hp.description}
                      onChange={(e) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.HomeFindTour, {
                            description: e.target.value,
                          })
                        )
                      }
                    />
                  </Field>
                  <label className="gap-phx-2 text-phx-sm flex items-center font-bold">
                    <Checkbox
                      checked={hp.showPriceFrom}
                      onCheckedChange={(v) =>
                        setPlacements(
                          updatePlacement(placements, WebFilterPlacementKind.HomeFindTour, {
                            showPriceFrom: v,
                          })
                        )
                      }
                    />
                    {t('webFilter.display.showPriceFrom')}
                  </label>
                </div>
              </SectionToggleRow>

              <p className="text-phx-xs text-phx-text-muted">{t('webFilter.display.hint')}</p>
            </div>
          );
        }}
      </form.Subscribe>
    </Panel>
  );
}
